import { ReactElement, useEffect, useMemo, useState } from 'react';
import { get, post } from 'utils/network';
import MealKitIcon from 'icons/icon-circle--meal-kit.svg';
import RteIcon from 'icons/icon-circle--ready-to-eat.svg';
import DiversifiedIcon from 'icons/icon-circle--mix-and-match.svg';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from '../../hooks/useCurrentUser';
import useUpcomingMenus from '../../hooks/useUpcomingMenus';
import Plan, { PlanName, PlanType, Programs } from '../../models/Plan';
import useAsyncEffect from '../../hooks/useAsyncEffect';
import IPlanInfo from '../../models/IPlanInfo';
import { ApiRoute, FeatureFlag } from '../../config';
import SelectableImageTile from '../shared/SelectableImageTile';
import {
  DEFAULT_MEAL_KIT_EXTRAS,
  DEFAULT_NO_EXTRAS,
  DEFAULT_PREPARED_EXTRAS,
  DEFAULT_SERVINGS,
  LIST_ITEMS_BY_PLAN_TYPE,
  PREFERENCES,
  PREFERENCES_BY_PLAN_TYPE,
} from '../UpdatePlan/utils/constants';
import SelectableServingQuantity from '../SignUp/SelectPlan/SelectableServingQuantity';
import ItemizedList from '../shared/ItemizedList';
import SelectableExtrasQuantity from '../SignUp/SelectPlan/SelectableExtrasQuantity';
import { toCurrency } from '../../utils/numbers';
import DropdownWithIcon from '../shared/DropdownWithIcon';
import useNotifications from '../../hooks/useNotifications';
import { type INotification, NotificationType } from '../../models/Notifications';
import PlansBanner from '../shared/banners/PlansBanner';
import { weekAndMonthDay } from '../../utils/dateTimes';
import AddOnTiles from './AccountSubscription/AddOnTiles';

function SubscriptionPage(): ReactElement {
  const { reload: reloadUser } = useCurrentUser();
  const { reload: reloadMenus } = useUpcomingMenus();
  const { currentUser } = useCurrentUser();
  const { subscription } = currentUser;
  const chefsChoicePlan = 'chefs_choice_plus';

  const plan: Plan = useMemo(
    () => new Plan(
      subscription?.plan,
      subscription?.extras,
      subscription?.is_diversified,
    ),
    [subscription],
  );

  const navigate = useNavigate();
  const {
    notifications,
    dispatch,
  } = useNotifications();
  const { planChange } = notifications;
  const [selectedPlan, setSelectedPlan] = useState(plan);

  const [isPlanInitialized, setIsPlanInitialized] = useState(Plan.isInitialized);
  const [selectedMealKitServings, setSelectedMealKitServings] = useState(DEFAULT_SERVINGS);
  const [selectedMealKitExtras, setSelectedMealKitExtras] = useState(DEFAULT_MEAL_KIT_EXTRAS);
  const [selectedPreparedExtras, setSelectedPreparedExtras] = useState(DEFAULT_PREPARED_EXTRAS);
  const [isSaving, setIsSaving] = useState(false);

  // TODO: much of this code is duplicated from UpdatePlan/index.tsx
  // It would be better to abstract the shared UI that could be used as a component on a page or in
  // a modal
  const showServingsToggle = [chefsChoicePlan, 'four_servings'].includes(selectedPlan.name)
    && selectedPlan.type !== PlanType.DIVERSIFIED;
  const showExtrasToggle = [PlanType.MEAL_KIT, PlanType.PREPARED].includes(selectedPlan.type);
  const isPlanUnmodified = selectedPlan.name === plan.name && plan.type === selectedPlan.type
    && (plan.selectedExtras.dinner === selectedMealKitExtras.dinner
      || plan.selectedExtras.dinner === selectedPreparedExtras.dinner);
  const isExtrasModalEnabled = currentUser.hasFeatureEnabled(FeatureFlag.EXTRAS_MODAL);

  const handlePlanChange = (planName: PlanName, isDiversified = false) => {
    let newPlan: Plan;
    switch (planName) {
      case 'jumpstart_program':
        newPlan = Plan.getPlan(1, DEFAULT_NO_EXTRAS, Programs.JUMPSTART_PROGRAM);
        break;
      case 'prepared_one_serving':
        newPlan = Plan.getPlan(1, selectedPreparedExtras);
        break;
      case 'four_servings':
        setSelectedMealKitServings(4);
        newPlan = Plan.getPlan(4, selectedMealKitExtras, planName);
        break;
      default:
        setSelectedMealKitServings(2);
        newPlan = Plan.getPlan(2, selectedMealKitExtras, planName, isDiversified);
    }
    setSelectedPlan(newPlan);
  };

  // only "chef's choice" gives the option of 2 vs. 4 servings (4 servings = 'four-servings' plan)
  const handleMealKitServingChange = (servings: number) => {
    let newPlan = selectedPlan;
    if (servings === 4) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras);
    } else if (servings === 2) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras, chefsChoicePlan);
    }
    setSelectedMealKitServings(servings);
    setSelectedPlan(newPlan);
  };

  const handleExtrasChange = (extraDinners: number) => {
    const selectedExtras = {
      dinner: extraDinners,
      breakfast: 0,
      lunch: 0,
    };
    const newPlan = Plan.getPlan(
      selectedPlan.servings,
      selectedExtras,
      selectedPlan.name,
      selectedPlan.isDiversified,
    );
    if (newPlan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitExtras(selectedExtras);
    } else {
      setSelectedPreparedExtras(selectedExtras);
    }
    setSelectedPlan(newPlan);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    await post(`${ApiRoute.USERS}/me/subscription`, {
      subscription: {
        plan: selectedPlan.name,
        extra_meals: selectedPlan.isDiversified ? DEFAULT_NO_EXTRAS : selectedPlan.selectedExtras,
        is_diversified: selectedPlan.isDiversified,
      },
    });
    dispatch({
      type: 'addNotification',
      notification: {
        notification_type: NotificationType.PLAN_CHANGE,
      } as INotification,
    });
    reloadUser();
    reloadMenus();
    setIsSaving(false);
    // Want the banner to auto-dismiss after 3 seconds
    setTimeout(() => {
      dispatch({
        type: 'removeNotifications',
        notificationType: NotificationType.PLAN_CHANGE,
      });
    }, 3000);
  };

  // Want to make sure we have the correct data in local state when we update the plan
  useEffect(() => {
    setSelectedPlan(plan);

    if (plan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitServings(plan.servings);
      setSelectedMealKitExtras(plan.selectedExtras);
    } else if (plan.type === PlanType.PREPARED) {
      setSelectedPreparedExtras(plan.selectedExtras);
    }
  }, [plan]);

  // We need a plan to be initialized before it is usable
  useAsyncEffect(async (isMounted) => {
    reloadUser();
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);

      if (response.parsedBody) {
        Plan.initialize(response.parsedBody);
        if (isMounted) setIsPlanInitialized(true);
      }
    }
  }, []);
  return (
    <div
      className="row c-my-account__profile-container"
    >
      {isPlanInitialized && (
        <div
          className="d-flex row g-1 c-my-account__profile__panel"
        >
          <div className="d-flex gap-4 col-lg-6 justify-content-center">
            <div
              id="editPlanPanel"
              className="c-my-account__profile c-my-account__profile__container"
            >
              <div className="c-card c-my-account__profile__card">
                <div className="c-card__content">
                  <div className="c-update-plan__account-page">
                    <span className="c-my-account__profile__panel-header">Plan</span>
                    <hr className="c-my-account__profile__header-line" />

                    <div
                      className="c-update-plan__plans-container c-my-account__profile__plans-container"
                    >
                      <SelectableImageTile
                        label="Meal Kit"
                        value={PlanType.MEAL_KIT}
                        isSelected={selectedPlan.type === PlanType.MEAL_KIT}
                        imageUrl={MealKitIcon}
                        onAccountManagementPage
                        onClick={() => selectedPlan.type !== PlanType.MEAL_KIT
                          && handlePlanChange(chefsChoicePlan, chefsChoicePlan === 'chefs_choice_plus')}
                      />
                      <SelectableImageTile
                        label="Ready-to-Eat"
                        value={PlanType.PREPARED}
                        isSelected={
                          [PlanType.PREPARED,
                            PlanType.PROGRAMS,
                          ].includes(selectedPlan.type)
                        }
                        imageUrl={RteIcon}
                        onAccountManagementPage
                        onClick={() => ![
                          PlanType.PREPARED,
                          PlanType.PROGRAMS].includes(selectedPlan.type)
                          && handlePlanChange('prepared_one_serving', false)}
                      />
                      <SelectableImageTile
                        label="Mix & Match"
                        value={PlanType.DIVERSIFIED}
                        isSelected={selectedPlan.type === PlanType.DIVERSIFIED}
                        onAccountManagementPage
                        imageUrl={DiversifiedIcon}
                        onClick={() => selectedPlan.type !== PlanType.DIVERSIFIED
                          && handlePlanChange('chefs_choice', true)}
                      />
                    </div>

                    <DropdownWithIcon
                      selectedOption={PREFERENCES[selectedPlan.name]}
                      options={(PREFERENCES_BY_PLAN_TYPE[selectedPlan.type] || []).filter(
                        (option) => option.value !== 'jumpstart_program',
                      )}
                      title={[PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type) ? 'Program' : 'Dietary Preference'}
                      onSelect={(preference) => handlePlanChange(
                        preference.value, selectedPlan.type === PlanType.DIVERSIFIED,
                      )}
                    />

                    <div className="c-update-plan__servings-container">
                      {showServingsToggle ? (
                        <>
                          <hr />
                          <SelectableServingQuantity
                            planType={selectedPlan.type}
                            selectedServings={selectedMealKitServings}
                            handleSelectServings={handleMealKitServingChange}
                            planName={selectedPlan.name}
                          />
                        </>
                      ) : (
                        <ItemizedList
                          list={LIST_ITEMS_BY_PLAN_TYPE[selectedPlan.type]}
                          planName={selectedPlan.name}
                        />
                      )}

                      {showExtrasToggle && selectedPlan.name !== 'chefs_choice_plus' && (
                        <>
                          <hr />
                          <SelectableExtrasQuantity
                            plan={selectedPlan}
                            handleSelectExtras={handleExtrasChange}
                            showMostPopularWidget={false}
                          />
                          <hr />
                        </>
                      )}

                      {selectedPlan.name === 'chefs_choice_plus' && (
                        <ItemizedList
                          list={LIST_ITEMS_BY_PLAN_TYPE[PlanType.DIVERSIFIED]}
                          planName={selectedPlan.name}
                        />
                      )}

                      {!showServingsToggle && !showExtrasToggle && (<hr />)}
                    </div>

                    <div className="c-update-plan__price">
                      <span className="u-text--md">Weekly Price</span>
                      <span className="u-text--md">{toCurrency(selectedPlan.planPrice)}</span>
                    </div>

                    <div className="c-my-account__profile__submit-btn">
                      <button
                        type="button"
                        className="c-button--primary"
                        disabled={isSaving || isPlanUnmodified || currentUser.isDeactivated}
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        Save Changes
                      </button>
                    </div>
                    {planChange && <PlansBanner />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex gap-4 col-lg-6 justify-content-center align-self-start">
            <div className="col col-md-6 col-lg-6 c-my-account__profile__panel__right">
              {!(selectedPlan.type === PlanType.DIVERSIFIED) && isExtrasModalEnabled && (
                <div className="c-card c-my-account__profile__card">
                  <div className="c-card__content">
                    <span className="c-my-account__profile__panel-header">Extras</span>
                    <hr className="c-my-account__profile__header-line" />
                    <p>
                      Select add-ons you would like to see in your future orders.
                      Add or remove at any time.
                    </p>
                    <AddOnTiles selectedPlanGiven={selectedPlan} />
                  </div>
                </div>
              )}
              <div
                className="c-my-account__profile c-my-account__profile__container c-my-account__profile__edit-sub"
              >
                <div className="c-card c-my-account__profile__card">
                  <div className="c-card__content">
                    <span className="c-my-account__profile__panel-header">Subscription Status</span>
                    <hr className="c-my-account__profile__header-line" />
                    <p>
                      Your subscription is&nbsp;
                      {subscription?.status}
                      .
                      {' '}
                      {currentUser.isReturning && (`Next delivery is scheduled for ${weekAndMonthDay(subscription.next_scheduled_order_date)}.`)}
                    </p>
                    <div className="c-my-account__profile__submit-btn">
                      <button
                        type="button"
                        className="c-button--primary"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/manage-subscription');
                        }}
                      >
                        Change subscription
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPage;
